/**
 * Tiny i18n layer.
 *
 * Strings live in two dictionaries (FI/EN). Anything in the HTML annotated
 * with `data-i18n="key"` (textContent) or `data-i18n-placeholder="key"`
 * (input placeholder) is automatically swapped when the active language
 * changes. Dynamic strings (e.g. listing column headers rendered by
 * TypeScript) use the `t()` helper directly.
 *
 * The selected language is persisted in `localStorage` under `smt_lang` so a
 * refresh keeps the user's preference. This matches the mockup behaviour
 * (`vertailutyokalu.html`, `setLanguage()` function).
 */
export type Lang = 'fi' | 'en';

const LS_KEY = 'smt_lang';

type Dict = Record<string, string>;

const FI: Dict = {
  // Branding
  brand_mark: 'Suomen Metsätilastot Oy',
  brand_sub: 'Vertailutyökalu · Comparable-based pricing',

  // Auth
  auth_login: 'Kirjaudu',
  auth_register: 'Rekisteröidy',
  nav_my_estates: 'Omat tilat',
  nav_institution: 'Markkinayhteenveto',
  nav_admin: 'Käyttäjähallinta',
  nav_logout: 'Kirjaudu ulos',
  role_admin: 'Admin',
  role_institution: 'Instituutio',
  // Tasolabelit yhtenevät BE:n level-semantiikan kanssa (asiakaspalaute
  // 31.5.2026 / migraatio 023). level=0 = "Odottava" (status=pending,
  // ei näy admin-dropdownissa). level=1..4 = asiakkaan käyttämät tasot.
  level_0: 'Odottava',
  level_1: 'Taso 1',
  level_2: 'Taso 2',
  level_3: 'Taso 3 (Pro)',
  level_4: 'Työntekijä',

  // Search
  search_label: 'Hae arvioitavaa tilaa',
  search_placeholder: 'Tunnus, nimi tai kunta — esim. 305-408-6-6, Kotaniemi, Kuusamo',

  // Listing
  listing_title: 'Myynnissä olevat metsätilat',
  listing_sub: 'Metsätilat, joissa on tarjousaikaa vielä jäljellä',
  listing_count_one: '{n} tila',
  listing_count_many: '{n} tilaa',
  listing_empty: 'Ei tuloksia annetuilla suodattimilla.',

  // Filters
  filter_region: 'Maakunta',
  filter_all: '— kaikki —',
  filter_min_ha: 'Min. metsämaa (ha)',
  filter_min_price: 'Min. pyyntihinta (€)',
  filter_min_m3: 'Min. m³/ha',
  filter_sort: 'Lajittele',
  filter_reset: 'Tyhjennä',
  filter_sale_status: 'Tilanne',
  sale_status_all: 'Kaikki',
  sale_status_active: 'Vain myynnissä',
  sale_status_closed: 'Toteutuneet',

  sort_deadline: 'Tarjousaika (lähin ensin)',
  sort_ha_desc: 'Metsämaa (suurin ensin)',
  sort_ha_asc: 'Metsämaa (pienin ensin)',
  sort_price_desc: 'Pyyntihinta (suurin ensin)',
  sort_price_asc: 'Pyyntihinta (pienin ensin)',
  sort_m3ha_desc: 'm³/ha (suurin ensin)',
  sort_eurha_asc: '€/ha (pienin ensin)',
  sort_eurha_desc: '€/ha (suurin ensin)',

  // Table columns — match mockup vertailutyokalu.html:7507-7516
  th_id: 'Tunnus',
  th_name: 'Tilan nimi',
  th_municipality: 'Kunta',
  th_region: 'Maakunta',
  th_total_ha: 'Pinta-ala',
  th_total_m3: 'Puustoa',
  th_m3ha_short: 'm³/ha',
  th_eurha_short: '€/ha',
  th_price: 'Pyyntihinta',
  th_deadline: 'Deadline',

  // Detail header
  detail_back: 'Takaisin listaan',
  detail_deadline_label: 'Tarjouksen deadline',
  detail_status_unsold: 'Myytävänä',
  detail_status_sold: 'Myyty',
  detail_listing_link: 'Siirry myynti-ilmoitukseen',
  detail_share: 'Jaa arvio',

  // Detail KPI cards (mockup vertailutyokalu.html, makeDetail labels)
  d_forest_ha: 'Metsämaa',
  d_total_ha: 'Kokonaispinta-ala',
  d_asking: 'Hintapyyntö',
  d_summa: 'Summa-arvo',
  d_timber_value: 'Puuston arvo',
  d_eur_ha: 'Metsämaanhinta €/ha',
  d_m3ha: 'Puusto m³/ha',
  d_eur_m3_asking: '€/m³ pyynnillä',
  d_ainespuu: 'Ainespuuta',
  d_tukki_m3: 'Tukkipuuta',
  d_tukki_pct: 'Tukkipuun osuus',
  d_broker: 'Välittäjä',
  d_kauppahinta: 'Toteutunut kauppahinta',
  d_unknown: '—',

  // Detail extras
  detail_region: 'Maakunta',
  detail_municipality: 'Kunta',
  detail_location: 'Sijainti',
  detail_forest_area: 'Metsämaa',
  detail_timber: 'Puusto',
  detail_price: 'Pyyntihinta',
  detail_sum_value: 'Summa-arvio',
  detail_kauppahinta: 'Toteutunut kauppahinta',
  detail_source: 'Lähde',
  detail_source_open: 'avaa',

  // AI analysis section
  ai_analysis_title: 'AI-analyysi',
  ai_analysis_empty: 'AI-analyysiä ei ole vielä saatavilla tälle tilalle.',
  ai_analysis_translating: 'käännetään…',

  // Map section
  map_title: 'Kohteen sijainti',
  map_sub: 'Kohteen kiinteistö kartalla, {kunta}.',
  map_sub_anonymous: 'Kohteen alueellinen sijainti — maakuntakeskus.',
  map_attribution: '© Maanmittauslaitos',
  layer_terrain: 'Maastokartta',
  layer_map: 'Taustakartta',
  layer_aerial: 'Ortokuva',
  layer_parcels: 'Kiinteistörajat',
  layer_parcel_ids: 'Kiinteistötunnukset',

  // Description / remarks
  detail_description: 'Kuvaus',
  detail_remarks: 'Huomiot',

  // Navigation views
  view_my_estates_title: 'Omat tilat',
  view_my_estates_sub: 'Sinulle jaetut tilat ja itse tallentamasi kohteet.',
  view_my_estates_empty: 'Ei vielä omia tiloja. Tilan jakaminen ja tallentaminen tulossa M5-vaiheessa.',
  view_market_title: 'Markkinayhteenveto — Suomen metsätilakaupat',
  view_market_sub: 'Kvartaali- ja vuosikohtainen yhteenveto tilojen koosta, hinnoittelusta ja saatavuudesta.',
  view_market_empty: 'Markkinakatsaus tulossa M6-vaiheessa.',
  // M6 Markkinayhteenveto (institution dashboard) — port of mockup keys.
  inst_title: 'Markkinayhteenveto — Suomen metsätilakaupat',
  inst_subtitle: 'Kvartaali- ja vuosikohtainen yhteenveto tilojen koosta, hinnoittelusta ja saatavuudesta',
  inst_mode_unsold: 'Myyntiin tulleet',
  inst_mode_sold: 'Toteutuneet kaupat',
  filter_kunta: 'Kunta',
  filter_kunta_ph: 'esim. Rovaniemi',
  filter_min_ha_long: 'Min. metsämaa (ha)',
  filter_max_ha: 'Max. metsämaa (ha)',
  filter_min_m3ha: 'Min. m³/ha',
  filter_n_regions: '{n} maakuntaa valittu',
  inst_quart_title: 'Kvartaaleittain — pinta-ala ja kokonaisarvo',
  inst_quart_sub: 'Viimeiset {n} kvartaalia · {first} → {last}',
  inst_quart_no_data: 'Ei dataa rajauksella',
  inst_legend_ha: 'Pinta-ala (ha)',
  inst_legend_eur: 'Kokonaisarvo (M€)',
  inst_yearly_title: 'Vuosittain',
  inst_yearly_sub: 'Yhteenveto vuosittain — {n} vuotta',
  inst_region_title: 'Maakuntakohtainen yhteenveto',
  inst_region_sub: 'Tämänhetkisessä suodatuksessa olevien tilojen jakauma maakunnittain',
  inst_buyer_title: 'Ostajatyyppien jakauma vuosittain',
  inst_buyer_sub: 'Instituutioiden osuus toteutuneista kaupoista',
  inst_buyer_no_data: 'Ei ostajatietoja',
  inst_kpi_avg_title: 'Keskimäärin vuodessa ({y1}–{y2})',
  inst_kpi_no_full_years: 'Keskimäärin vuodessa — ei dataa täysiltä vuosilta',
  inst_kpi_sold_count: 'Toteutuneita kauppoja',
  inst_kpi_unsold_count: 'Myyntiin tulleita kohteita',
  inst_kpi_total_ha: 'Yhteenlaskettu pinta-ala',
  inst_kpi_total_value: 'Kokonaisarvo ({label})',
  inst_kpi_avg_eur_ha: 'Keskim. €/ha',
  inst_kpi_unit_count: '{mode} / vuosi',
  inst_kpi_unit_ha: 'ha / vuosi',
  inst_kpi_unit_eur: 'M€ / vuosi',
  inst_kpi_unit_eur_ha: '€/ha',
  inst_count_sold: 'kauppaa',
  inst_count_unsold: 'kohdetta',
  inst_price_sold: 'Toteutunut kauppasumma',
  inst_price_unsold: 'Pyyntihinta',
  inst_year_total_ha: 'Pinta-ala yht.',
  inst_year_total_sum_sold: 'Kauppasumma',
  inst_year_total_sum_unsold: 'Kokonaispyynti',
  inst_year_median_eurha: 'Mediaani €/ha',
  inst_year_median_m3: 'Mediaani m³/ha',
  inst_year_median_tukki: 'Mediaani tukki%',
  inst_year_top_regions: 'Suosituimmat maakunnat: <strong>{regions}</strong>',
  inst_year_buyers: 'Ostajat: <strong>{inst}</strong> institutionaalista · <strong>{priv}</strong> yksityistä ({pct} % instituutio)',
  inst_region_th_region: 'Maakunta',
  inst_region_th_count: 'Kohteita',
  inst_region_th_ha: 'Yht. ha',
  inst_region_th_sum_sold: 'Yht. kauppasumma (M€)',
  inst_region_th_sum_unsold: 'Yht. pyynti (M€)',
  inst_region_th_eurha: '€/ha keskim.',
  inst_buyer_info: '{pct} % inst · {inst}/{total}',
  inst_no_data: 'Ei dataa',
  market_forbidden: 'Markkinayhteenveto vaatii Pro-tason tunnukset.',
  // M6 saved searches
  nav_saved_searches: 'Tallennetut haut',
  saved_searches_pick: '— Tallennettu haku —',
  saved_searches_save: 'Tallenna haku',
  saved_searches_delete: 'Poista',
  saved_searches_delete_confirm: 'Poistetaanko tämä tallennettu haku?',
  saved_searches_name_prompt: 'Anna nimi tallennettavalle haulle:',
  saved_searches_title: 'Tallennetut haut',
  saved_searches_sub: 'Hallitse aiemmin tallentamiasi suodatinjoukkoja.',
  saved_searches_empty: 'Ei vielä tallennettuja hakuja.',
  saved_searches_th_name: 'Nimi',
  saved_searches_th_filters: 'Suodattimet',
  saved_searches_th_updated: 'Päivitetty',
  login_required: 'Kirjautuminen vaaditaan.',
  view_admin_title: 'Käyttäjähallinta',
  view_admin_sub: 'Ylläpidä järjestelmän käyttäjiä',
  close: 'Sulje',
  // M6.2 — Käyttäjähallinta-modaalin välilehdet
  admin_tab_users: 'Käyttäjät',
  admin_tab_create: 'Uusi käyttäjä',
  admin_tab_shares: 'Jaetut tilat',
  admin_tab_orders: 'Tilaukset',
  admin_tab_feedback: 'Palaute',
  admin_tab_log: 'Tapahtumat',
  admin_tab_prompt: 'AI-analyysiohje',
  admin_prompt_sub:
    'Ohje, jonka mukaan tekoäly kirjoittaa metsätilan kannattavuusanalyysin. Muutos vaikuttaa uusiin ja uudelleen analysoitaviin tiloihin.',
  admin_prompt_label: 'Analyysiohje',
  admin_prompt_save: 'Tallenna ohje',
  admin_prompt_reset: 'Palauta oletusohje',
  admin_prompt_reset_confirm:
    'Palautetaanko toimitettu oletusohje? Nykyiset muutokset menetetään.',
  admin_prompt_reset_done: 'Oletusohje palautettu.',
  admin_prompt_saving: 'Tallennetaan…',
  admin_prompt_saved: 'Ohje tallennettu.',
  admin_prompt_empty: 'Ohje ei voi olla tyhjä — käytä "Palauta oletusohje".',
  admin_prompt_is_default: 'Käytössä on toimitettu oletusohje.',
  admin_prompt_is_custom: 'Käytössä on muokattu ohje',
  admin_prompt_test_title: 'Kokeile ohjetta',
  admin_prompt_test_sub:
    'Ajaa analyysin uudelleen yhdelle tilalle tallennetuista tiedoista. Ei hae ilmoitussivua uudelleen.',
  admin_prompt_estate_id: 'Tilan ID',
  admin_prompt_run: 'Analysoi uudelleen',
  admin_prompt_running: 'Analysoidaan… tämä voi kestää kymmeniä sekunteja.',
  admin_prompt_bad_id: 'Anna kelvollinen tilan ID.',
  admin_create_first_name: 'Etunimi',
  admin_create_last_name: 'Sukunimi',
  admin_create_email: 'Sähköposti',
  admin_create_password: 'Salasana (min. 8 merkkiä)',
  admin_create_organization: 'Organisaatio',
  admin_create_role: 'Rooli',
  admin_create_level: 'Taso',
  admin_create_regions: 'Sallitut maakunnat (Taso 1)',
  admin_create_submit: 'Luo käyttäjä',
  admin_create_ok: 'Käyttäjä luotu: {email}',
  admin_create_another: 'Luo uusi käyttäjä',
  admin_create_ok_name: 'Nimi',
  admin_create_ok_role: 'Rooli',
  admin_create_ok_level: 'Taso',
  admin_edit_title: 'Muokkaa käyttäjää: {name}',
  admin_edit_status: 'Tila',
  admin_edit_name: 'Etu- ja sukunimi',
  admin_edit_email: 'Sähköposti',
  admin_edit_password: 'Uusi salasana',
  admin_edit_password_hint: '(tyhjä = ei muutosta)',
  admin_edit_save: 'Tallenna muutokset',
  admin_edit_cancel: 'Peruuta',
  admin_edit_delete: 'Poista käyttäjä',
  admin_edit_delete_confirm: 'Poistetaanko käyttäjä "{name}" pysyvästi? Tätä ei voi perua.',
  admin_edit_delete_ok: 'Käyttäjä poistettu',
  admin_edit_delete_err: 'Poisto epäonnistui',
  admin_edit_ok: 'Tallennettu',
  admin_edit_err: 'Virhe tallennuksessa',
  // Tasojen täysselitteet admin-dropdowniin (asiakaspalaute 1.6 / kuva 2).
  // Semantiikka M-FB4:n mukainen (level 1–4); level 0 = odottava.
  level_0_full: 'Odottava — ei vielä hyväksytty',
  level_1_full: 'Taso 1 — Jaetut tilat + valitut maakunnat',
  level_2_full: 'Taso 2 — Kaikki myynnissä olevat tilat',
  level_3_full: 'Taso 3 (Pro) — Toteutuneet kaupat, markkinayhteenveto & datan vienti',
  level_4_full: 'Työntekijä — Pro + arvioiden jako',
  view_admin_btn_edit: 'Muokkaa',
  admin_shares_search_ph: 'Etsi jaolla: jakaja, vastaanottaja, tilatunnus tai tilan nimi…',
  admin_shares_filter_active: 'Aktiiviset',
  admin_shares_filter_revoked: 'Peruutetut',
  admin_shares_filter_all: 'Kaikki',
  admin_shares_empty: 'Ei jakoja valitulla suodattimella.',
  admin_shares_th_created: 'Luotu',
  admin_shares_th_estate: 'Tila',
  admin_shares_th_sharer: 'Jakaja',
  admin_shares_th_recipient: 'Vastaanottaja',
  admin_shares_th_status: 'Tila',
  admin_shares_th_message: 'Viesti',
  admin_shares_active: 'Aktiivinen',
  admin_shares_revoked: 'Peruutettu',
  view_admin_tab_pending: 'Odottavat',
  view_admin_tab_active: 'Aktiiviset',
  view_admin_tab_all: 'Kaikki',
  view_admin_th_name: 'Nimi',
  view_admin_th_email: 'Sähköposti',
  view_admin_th_role: 'Rooli',
  view_admin_th_level: 'Taso',
  view_admin_th_status: 'Tila',
  view_admin_th_created: 'Luotu',
  view_admin_th_actions: 'Toiminnot',
  view_admin_btn_approve: 'Hyväksy',
  view_admin_btn_reject: 'Hylkää',
  view_admin_loading: 'Ladataan käyttäjiä…',
  view_admin_empty: 'Ei käyttäjiä valitulla suodattimella.',
  view_admin_err: 'Käyttäjälistan haku epäonnistui',
  view_admin_approved: 'Hyväksytty.',
  view_admin_rejected: 'Hylätty.',

  // Valuation — mockup-aligned (estimate-section + bid-grid + tabs)
  val_title: 'Vertailuhinta-arvio',
  val_offer_title_n: 'Tarjous — {n} parasta vertailutilaa',
  val_btn_asking: 'Pyynti-suhdeluku',
  val_btn_summa: 'Summa-arvo-suhdeluku',
  val_bid_conservative: 'Konservatiivinen',
  val_bid_recommended: 'Suositus (mediaani)',
  val_bid_aggressive: 'Aggressiivinen',
  val_vs_asking_pct: 'vs pyynti',
  val_ratio_x: 'ratio {r}x',
  val_conf_high: 'Korkea luotettavuus',
  val_conf_mid: 'Kohtuullinen luotettavuus',
  val_conf_low: 'Matala luotettavuus',
  val_conf_explanation:
    '{hard} vertailukauppaa (sama maakunta + m³/ha ±20 %, samankaltaisuus {sim} %)',
  val_based_on: 'Pohjana',
  val_alternative_label: 'Vaihtoehtoinen arvio',
  val_via_summa: 'summa-arvon kautta',
  val_via_asking: 'pyyntihinnan kautta',
  val_anchor_asking: 'Pyyntihinta-ankkurilla',
  val_anchor_summa: 'Summa-arvio-ankkurilla',
  val_anchor_label: 'Ankkuri',
  val_ratio: 'suhde',
  val_eur_per_ha: '€/ha (mediaani vertailuista)',
  val_confidence: 'luottamus',
  val_n_comps_one: '{n} vertailutila',
  val_n_comps_many: '{n} vertailutilaa',
  val_similarity: 'samankaltaisuus',
  val_insufficient: 'Vertailutiloja ei löytynyt tarpeeksi tämän tilan hinta-arvion laskemiseen.',
  val_forbidden: 'Vertailupohjainen hinta-arvio vaatii vähintään Taso 2 -tunnuksen.',

  // Sold estate view (Toteutunut kauppa vs malliarvio)
  val_actual_vs_model: 'Toteutunut kauppa vs. malliarvio',
  val_asking_price: 'Hintapyyntö',
  val_actual_price: 'Toteutunut kauppahinta',
  val_model_estimate: 'Malliarvio (suositushinta)',
  val_overshoots: 'ylittää malliarvion',
  val_undershoots: 'alittaa malliarvion',
  val_by_pct: '{pct} prosentilla',
  val_diff_eur: 'ero {eur}',
  val_accuracy_good: '— malli ennusti tämän kaupan hyvin.',
  val_accuracy_mid: '— mallin ennuste oli kohtuullinen.',
  val_accuracy_bad: '— mallin ennuste poikkesi merkittävästi.',
  val_based_on_asking: 'Hintapyyntö',
  val_no_comps: 'Ei vertailutiloja',

  // Reasoning headline (mockup `renderRecommendation`)
  reason_label: 'Suosituksen perustelut',
  reason_method_asking: 'Pyyntihintapohjainen arvio',
  reason_method_summa: 'Summa-arvopohjainen arvio',
  median_estimate_is: 'Mediaaniarvio on',
  pct_above: 'noin {pct} % yli {basis}',
  pct_below: 'noin {pct} % alle {basis}',
  near_basis: 'lähellä {basis}',
  of_asking: 'pyyntihinnasta',
  of_summa: 'summa-arvosta',
  ratio_coef: '(kerroin {r}x).',
  strategy_aggressive_html:
    'Vahvat positiiviset signaalit puoltavat aggressiivisempaa tarjousta — harkitse <strong>{eur}</strong>.',
  strategy_conservative_html:
    'Useita riskitekijöitä — pysy lähellä konservatiivista arviota <strong>{eur}</strong>.',
  strategy_balanced_html: 'Tasapainoinen tilanne — suositusarvio <strong>{eur}</strong> on hyvä lähtökohta.',
  method_explain_asking_html:
    'Suositus perustuu <strong>{n} vertailutilaan</strong> samalla alueella, joiden m³/ha-taso vastaa tämän kohteen tasoa. Mediaaniarvio painottaa eniten niitä vertailutiloja, joiden tukkiprosentti, kasvupaikkajakauma ja kehitysluokat ovat samankaltaisia.',
  method_explain_summa_html:
    'Suositus perustuu <strong>{n} vertailutilaan</strong>, joiden kauppahinta-summa-arvo-suhdeluku on huomioitu. Tämä menetelmä mittaa, kuinka paljon markkina maksaa todellisesta arvosta — kohteen oma hintapyyntö ei vaikuta lopputulokseen.',
  spread_explain:
    'Konservatiivinen ja aggressiivinen arvio kuvaavat tarjouskertoimien hajontaa vertailupohjassa (±1 keskihajonta).',
  bid_higher_if: 'Tarjoa rohkeammin:',
  bid_lower_if: 'Tarjoa varovaisemmin:',
  no_signals:
    'Ei merkittäviä yli- tai aliarviointia puoltavia signaaleja. Pysy suositusarviossa.',

  // Buyer mix (Instituutio-kiinnostus)
  inst_label: 'Instituutio-<br>kiinnostus',
  inst_focus_area: 'Instituutio-ostajien fokusalue',
  inst_likely: 'Instituutio-ostajia mukana',
  inst_borderline: 'Rajatapaus',
  inst_unlikely: 'Pääosin yksityismarkkina',
  inst_private: 'Yksityismarkkina',
  inst_avg: 'Maakunnan keskiarvo',
  inst_comment_strong:
    'Vahva instituutio-ostajien fokusalue — {pct} % tämän kokoluokan kohteista myydään instituutiolle tällä alueella',
  inst_comment_above_strong:
    'Tila ylittää alueellisen instituutiokiinnostuksen rajan (≥{threshold} ha) — {pct} % vastaavan kokoisista kohteista myydään instituutiolle',
  inst_comment_above:
    'Tila ylittää alueellisen instituutiokiinnostuksen rajan (≥{threshold} ha) — {pct} % vastaavan kokoisista kohteista myydään instituutiolle',
  inst_comment_border:
    'Rajatapaus — tila on hieman alle alueellisen instituutiokiinnostuksen rajan (≥{threshold} ha). Vastaavan kokoisista kohteista vain {pct} % myydään instituutiolle',
  inst_comment_unlikely:
    'Tämän kokoinen tila kiinnostaa instituutio-ostajia harvoin tällä alueella — instituutio-osuus tämän kokoluokan kohteissa vain {pct} %. Alueellinen kynnys on ≥{threshold} ha',
  inst_comment_private:
    'Yksityinen markkina — instituutio-ostajat eivät yleensä osallistu tämän maakunnan kohteisiin (vain {pct} % kohteista myydään instituutiolle, koosta riippumatta)',
  inst_comment_avg: 'Maakunnan keskiarvo: {pct} % instituutio-ostajia',
  inst_n_eq: '(n={n})',

  // Reasoning items (8 rules — pluses & minuses)
  r_inst_strong:
    'Tämän kokoinen tila tällä alueella on instituutio-ostajien aktiivisesti tavoittelema ({pct} % vastaavista kohteista myydään instituutiolle) — useita ammattisijoittajia kilpailee, nostaa tarjouksia keskimääräistä korkeammalle',
  r_inst_likely:
    'Tila ylittää alueellisen instituutiokiinnostuksen rajan (≥{threshold} ha) — instituutio-ostajat osallistuvat kilpailuun ({pct} % vastaavista kohteista)',
  r_inst_unlikely:
    'Tila ei ylitä alueellista instituutiokiinnostuksen rajaa (≥{threshold} ha) — vain {pct} % tämän kokoluokan kohteista myydään instituutiolle, kilpailu rajoittuu paikallisiin yksityisostajiin',
  r_inst_private:
    'Maakunnassa instituutio-ostajat eivät yleensä osallistu (vain {pct} % kohteista koosta riippumatta) — kilpailu rajoittuu paikallisiin yksityisostajiin',
  r_asking_below_summa:
    'Pyyntihinta on poikkeuksellisen kaukana summa-arvosta (vain {pct} %) — myyjä on tehnyt isoja vähennyksiä, mikä voi houkutella useita ostajia',
  r_asking_above_summa:
    'Pyyntihinta lähellä tai yli summa-arvon ({pct} %) — myyjä ei ole tehnyt tavallisia summa-arvon vähennyksiä, kohde on hinnoiteltu kireästi',
  r_mature_high:
    'Hakkuukypsää ja varttunutta metsää {pct} % alasta — välitön hakkuumahdollisuus tukee korkeampaa tarjousta',
  r_mature_low: 'Vähän hakkuukypsää metsää ({pct} %) — tuottoa joutuu odottamaan, alentaa kohteen arvoa',
  r_log_high: 'Korkea tukkipuun osuus ({pct} %) — arvokkaampaa puuta kuin keskimäärin',
  r_log_low: 'Matala tukkipuun osuus ({pct} %) — pääosin kuitupuuta, alempi €/m³',
  r_density_high: 'Erittäin runsaspuustoinen ({m3} m³/ha) — kerää ostajia laajemmalta',
  r_density_low: 'Heikko puustotiheys ({m3} m³/ha) — odota tarjouksia vain alueellisilta toimijoilta',
  r_site_fertile:
    'Rehevä kasvupaikka ({pct} % lehtoa tai lehtomaista kangasta) — kasvunopeus keskimääräistä parempi',
  r_site_poor:
    'Karu kasvupaikka ({pct} % kuivaa, karukko- tai kallioista maata) — tuotto pitkällä aikavälillä matalampi',
  r_low_confidence:
    'Vain {n} vertailutilaa — arvio sisältää suurta epävarmuutta, harkitse manuaalista vertailua',
  r_shoreline: 'Oma ranta — virkistysarvo voi nostaa hintaa yli puhtaan metsätalousarvon',
  r_buildings: 'Tilalla rakennuksia — voi nostaa arvoa, mutta tarkista kunto',
  r_no_road: 'Ei tieyhteyttä perille — hakkuu- ja hoitokustannukset korkeammat',
  r_damage: 'Metsätuhoja todettu — voi vaatia uudistustöitä',
  r_protected: 'Suojelualuetta tilalla — rajoittaa metsätalouskäyttöä',
  comp_title: 'Vertailutilat',
  comp_title_n: 'Vertailutilat — TOP {n}',
  comp_subtitle_top20: 'Suodatus: sama maakunta · m³/ha ±20 % · pisteytys log% + kasvupaikat + kehitysluokat',
  comp_th_name: 'Tila',
  comp_th_municipality: 'Kunta',
  comp_th_score: 'Score',
  comp_th_area: 'Pinta-ala',
  comp_th_m3ha: 'm³/ha',
  comp_th_tukki: 'Tukki-%',
  comp_th_price: 'Pyynti',
  comp_th_kauppahinta: 'Toteutunut',
  comp_th_ratio: 'Pyynti-r',
  comp_th_summa_ratio: 'Summa-r',
  comp_th_eur_ha: '€/ha',
  comp_th_eur_m3: '€/m³',
  // comp_th_closed retained for backwards compatibility but no longer
  // rendered (audit v2 virhe 3, 2026-05-30 — Pvm column removed).
  comp_th_closed: 'Pvm',

  // Method report — laajennettu asiakaspalautteen perusteella 31.5.2026
  // matchaamaan referenssin "Menetelmäanalyysi · Pyynti- vs. summa-arvo-
  // pohjainen ennuste". Luvut SPEC_FI.md §11 (LOO-jälkitestaus, n=4 912).
  mr_title: 'Menetelmäanalyysi · Pyynti- vs. summa-arvopohjainen ennuste',
  mr_subtitle: 'Tausta-analyysi siitä, kumpi ennustemenetelmä on osunut paremmin toteutuneisiin kauppahintoihin',
  mr_summary_tag: 'YHTEENVETO',
  mr_summary_headline: 'Pyyntipohjainen ennuste osuu tarkemmin kuin summa-arvopohjainen koossa ja maakunnassa riippumatta. Pyyntipohjaisen mediaanivirhe on 9,0 %, summa-arvopohjaisen 11,1 %.',
  mr_summary_body: 'Testasimme molempia menetelmiä koko myytyjen tilojen aineistolla (4 912 tilaa, joilla molemmat ennusteet voitiin laskea). Jokaista tilaa kohden ennuste laskettiin sulkemalla kyseinen tila pois vertailupohjasta, joten kyseessä on aito jälkitestaus. Vertailutilojen valintaan käytetään ±20 % m³/ha-haitaria, joka osoittautui jälkitestauksessa optimaaliseksi.',
  mr_block_accuracy: 'Kokonaistarkkuus',
  mr_th_metric: 'Mittari',
  mr_th_asking: 'Pyyntipohj.',
  mr_th_summa: 'Summa-pohj.',
  mr_metric_mae: 'Keskimääräinen poikkeama (MAE)',
  mr_metric_median: 'Mediaanipoikkeama',
  mr_metric_p10: 'Ennusteita ±10 % sisällä toteumasta',
  mr_metric_p20: 'Ennusteita ±20 % sisällä toteumasta',
  mr_metric_eur: 'Keskim. virhe euroina',
  mr_metric_winner: 'Tilakohtaisesti parempi ennuste',
  mr_block_regions: 'Maakunnittain',
  mr_regions_body: 'Pyyntipohjainen ennuste voitti tarkkuudessa 15 maakunnassa 18:sta. Kolmessa maakunnassa (Uusimaa, Satakunta, Pirkanmaa) summa-arvopohjainen oli niukasti tarkempi. Vain Uusimaalla ero oli merkityksellinen (6,5 prosenttiyksikköä — tosin pienellä otoksella, n=62). Satakunnassa ja Pirkanmaalla ero oli alle 1,5 prosenttiyksikköä eli käytännössä tasapeliä. Pyynnin selvimmät voitot olivat Etelä-Karjalassa (+7,8 pp), Kainuussa (+6,5 pp) ja Pohjois-Karjalassa (+4,1 pp).',
  mr_block_size: 'Tilan koolla ei ole väliä',
  mr_size_th_class: 'Tilan koko',
  mr_size_th_n: 'N',
  mr_size_th_asking_mae: 'Pyynti MAE',
  mr_size_th_summa_mae: 'Summa MAE',
  mr_size_th_diff: 'Erotus',
  mr_size_lt10: 'alle 10 ha',
  mr_size_10_15: '10–15 ha',
  mr_size_15_20: '15–20 ha',
  mr_size_20_30: '20–30 ha',
  mr_size_30_50: '30–50 ha',
  mr_size_50_75: '50–75 ha',
  mr_size_75_100: '75–100 ha',
  mr_size_gt100: 'yli 100 ha',
  mr_size_body: 'Pyyntipohjainen voittaa kaikissa kokoluokissa 1,6—12,3 prosenttiyksikköllä. Marginaali on suurin alle 10 ha:n tiloilla (+12,3 pp, tosin n=12 on pieni) ja 50–75 ha:n tiloilla (+5,0 pp, n=490). Yli 10 ha:n tiloilla pyynnin etu on tasaisesti 1,6—5,0 prosenttiyksikköä — yhdessäkään kokoluokassa summa-arvopohjainen ei nouse edelle.',
  mr_why_title: 'Miksi pyyntipohjainen toimii paremmin?',
  mr_why_body: 'Myyjä tai välittäjä on jo hinnoitellut tilan ottaen huomioon mm. summa-arvon, alueen aiemmat kaupat, tilan rakennukset, tiet, rannan ja muut yksityiskohdat. Hintapyyntö kantaa siten paljon paikallista markkinatietoa, jota raaka summa-arvo ei tavoita. Ennustamalla "kerrointa pyyntihintaan" käytämme tätä asiantuntemusta hyväksi. Summa-arvo on objektiivisempi mutta menettää näiden piirteiden tiedon.',
  mr_recommendation_title: 'Suositus',
  mr_recommendation_lead: 'Käytä oletuksena pyyntipohjaista ennustetta.',
  mr_recommendation_body: 'Summa-arvopohjainen toimii hyödyllisenä ristivertailuna — kun kaksi ennustetta eroavat merkittävästi toisistaan (yli ±15 %), se on itsessään hyödyllinen signaali: joko myyjä on hinnoitellut tilan poikkeavasti, tai vertailupohjassa on jotain epätyypillistä.',
  mr_footer: 'Testimenetelmä: Leave-one-out -jälkitestaus. Jokaista myytyä tilaa kohden ennuste laskettiin sulkemalla kyseinen tila pois vertailupohjasta — sama logiikka kuin tämän työkalun toteuttaa myymättömille tiloille. Vertailutilojen valintaan käytettiin ±20 % m³/ha-haitaria. Otos: 4 912 tilaa joissa molemmat ennusteet voitiin laskea (vaatii summa-arvotiedon).',

  // Divergence notice (SPEC_FI.md §6)
  div_title: 'Menetelmät eri mieltä',
  div_msg_asking_higher:
    'Pyyntihinta-ankkurilla saatu arvio on {pct} % suurempi kuin summa-arvolla. Suositeltu nostotasoja {bump} pyynnistä.',
  div_msg_summa_higher:
    'Summa-arvo-ankkurilla saatu arvio on {pct} % suurempi kuin pyynnillä. Tilan puuston arvo viittaa korkeampaan toteutumiseen.',

  // Recommendation engine (SPEC_FI_FULL.md §5)
  rec_title: 'Päätelmä',
  rec_pluses: 'Plussat',
  rec_minuses: 'Miinukset',
  rec_strategy_aggressive: 'Aggressiivinen tarjous — ylärajalle saakka.',
  rec_strategy_conservative: 'Konservatiivinen tarjous — alarajalla.',
  rec_strategy_balanced: 'Tasapainoinen tarjous — mediaaniarviolla.',

  // Profile bars (SPEC_FI_FULL.md §7)
  prof_species_title: 'Puulajijakauma',
  prof_species_empty: 'Ei puulajijakaumadataa.',
  prof_site_title: 'Kasvupaikkajakauma',
  prof_site_empty: 'Ei kasvupaikkadataa.',
  prof_dev_title: 'Kehitysluokkajakauma',
  prof_dev_empty: 'Ei kehitysluokkadataa.',
  prof_unit_m3: '{v} m³',

  // Login modal
  login_title: 'Suomen Metsätilastot Oy',
  login_sub: 'Vertailutyökalu — kirjautuminen',
  login_email: 'Sähköposti',
  login_password: 'Salasana',
  login_submit: 'Kirjaudu sisään',
  login_submitting: 'Kirjaudutaan…',
  login_noaccount: 'Ei vielä tunnusta?',
  login_register_here: 'Rekisteröidy tästä',
  login_err_missing: 'Anna sähköposti ja salasana.',
  login_err_pending: 'Tunnuksesi odottaa vielä admin-hyväksyntää.',
  login_err_suspended: 'Tunnus on jäädytetty. Ota yhteyttä ylläpitoon.',
  login_err_wrong: 'Sähköposti tai salasana on virheellinen.',
  login_err_net: 'Verkkovirhe. Kokeile uudelleen.',

  // Register modal
  reg_title: 'Suomen Metsätilastot Oy',
  reg_sub: 'Rekisteröidy palveluun — admin hyväksyy tunnuksen',
  reg_firstname: 'Etunimi *',
  reg_lastname: 'Sukunimi *',
  reg_email: 'Sähköposti *',
  reg_password: 'Salasana *',
  reg_password2: 'Salasana uudelleen *',
  reg_terms: 'Hyväksyn yleiset ehdot ja annan Suomen Metsätilastot Oy:lle luvan käsitellä henkilötietojani GDPR:n mukaisesti.',
  reg_submit: 'Rekisteröidy',
  reg_submitting: 'Lähetetään…',
  reg_haveaccount: 'Onko sinulla jo tunnus?',
  reg_login_here: 'Kirjaudu sisään tästä',
  reg_err_missing: 'Täytä kaikki pakolliset kentät.',
  reg_err_short_password: 'Salasanan tulee olla vähintään 8 merkkiä.',
  reg_err_mismatch: 'Salasanat eivät täsmää.',
  reg_err_terms: 'Hyväksy ehdot ja GDPR-suostumus.',
  reg_success: 'Rekisteröityminen onnistui. Admin tarkistaa tunnuksesi ja saat sähköpostin kun se on hyväksytty.',

  // Register prompt
  prompt_title: 'Tila-arvion näet kirjautumalla palveluun.',
  prompt_register_pre: 'Rekisteröidy palveluun',
  prompt_register_here: 'tästä',
  prompt_or: 'tai',

  // Footer
  footer_version: 'v0.1.0 (M4-PoL)',
  footer_faq: 'FAQ',
  footer_feedback: 'Lähetä palautetta',
  footer_changelog: 'Mitä uutta',

  // FAQ modal — full categorised content lives in src/data/faq.ts (FAQ_DATA),
  // rendered by footer-modals.ts. Only the modal title is i18n'd here.
  faq_title: 'Usein kysytyt kysymykset',

  // Changelog modal
  changelog_title: 'Mitä uutta',
  changelog_body: `<h3>v0.1.0 — Toukokuu 2026 (M4 PoL)</h3>
<ul>
  <li>Mockupin tarkka portti: cream/gold-teema, Fraunces-otsikkofontti.</li>
  <li>Anonyymi-first: selailu ilman kirjautumista, kirjautuminen vasta arvion katsomiseen.</li>
  <li>Maanmittauslaitoksen kartta + kiinteistörajat detail-sivulla.</li>
  <li>FIN/ENG -kielivaihto + light/dark-teema, valinnat säilyvät.</li>
  <li>Käyttäjähallinta admineille (hyväksy / hylkää odottavat tunnukset).</li>
  <li>Comparable-pohjainen hinta-arvio + vertailutilojen taulu.</li>
</ul>`,

  // Feedback modal
  feedback_title: 'Lähetä palautetta',
  feedback_sub: 'Kuvaile mahdollisimman tarkasti: mitä yritit tehdä, mitä odotit, ja mitä tapahtui.',
  feedback_name: 'Nimi (valinnainen)',
  feedback_email: 'Sähköposti (valinnainen)',
  feedback_message: 'Viesti *',
  feedback_placeholder: 'Esim. Klikkasin tilaa X ja kartta ei latautunut…',
  feedback_submit: 'Lähetä palaute',
  feedback_submitting: 'Lähetetään…',
  feedback_err_empty: 'Kirjoita viesti ennen lähettämistä.',
  feedback_err_send: 'Palautteen lähetys epäonnistui. Yritä uudelleen.',

  // Seller submission form ("Myyjien ilmoitukset")
  seller_submission_title: 'Myyjän ilmoitus',
  seller_submission_sub:
    'Täytä tilan myyntitiedot. Ilmoitus lähetetään ylläpidon tarkistettavaksi ennen kuin se julkaistaan metsatilastot.fi-listalla.',
  seller_submission_submit: 'Lähetä ilmoitus',
  seller_submission_err_required: 'Täytä kaikki pakolliset kentät (*).',
  seller_submission_success: 'Kiitos! Ilmoitus on vastaanotettu ja odottaa ylläpidon tarkistusta.',

  // Seller submission form — field group titles
  ssf_g_perustiedot: 'Perustiedot',
  ssf_g_hintatiedot: 'Hintatiedot',
  ssf_g_lisatiedot: 'Lisätiedot',
  ssf_g_tilavuus: 'Tilavuus puulajeittain (m³)',
  ssf_g_maapohja: 'Maapohja (ha)',
  ssf_g_kasvupaikat: 'Kasvupaikat (ha)',
  ssf_g_kehitysluokat: 'Kehitysluokat (ha)',
  ssf_g_ravinteisuus: 'Kasvupaikkojen ravinteisuus (ha)',
  ssf_g_valittaja: 'Välittäjä',
  ssf_g_aikataulu: 'Aikataulu ja kuvaus',

  // Seller submission form — field labels
  ssf_property_id: 'Kiinteistötunnus',
  ssf_name: 'Tilan nimi',
  ssf_municipality: 'Kunta',
  ssf_region: 'Maakunta',
  ssf_area: 'Pinta-ala (ha)',
  ssf_forest_area: 'Metsäala (ha)',
  ssf_forest_type: 'Metsätyyppi',
  ssf_price: 'Pyyntihinta (€)',
  ssf_pelto_arvo: 'Peltojen arvo (€)',
  ssf_tontti_rakennus_arvo: 'Tonttien/rakennusten arvo (€)',
  ssf_muu_arvo: 'Muu arvo (€)',
  ssf_wood_value: 'Puuston arvo (€)',
  ssf_sum_value: 'Summa-arvo (€)',
  ssf_has_buildings: 'Rakennuksia',
  ssf_has_zoning: 'Kaava-alueella',
  ssf_zoning_type: 'Kaavan tyyppi',
  ssf_has_protected_area: 'Suojelualuetta',
  ssf_has_forest_damage: 'Metsätuhoja',
  ssf_has_road_access: 'Tieyhteys',
  ssf_hunting_agreement: 'Metsästysvuokrasopimus',
  ssf_encumbrances: 'Rasitteet',
  ssf_has_pelto: 'Peltoa',
  ssf_has_oma_ranta: 'Omaa rantaa',
  ssf_manty_kuitupuu: 'Mänty kuitupuu',
  ssf_manty_tukkipuu: 'Mänty tukkipuu',
  ssf_kuusi_kuitupuu: 'Kuusi kuitupuu',
  ssf_kuusi_tukkipuu: 'Kuusi tukkipuu',
  ssf_koivu_kuitupuu: 'Koivu kuitupuu',
  ssf_koivu_tukkipuu: 'Koivu tukkipuu',
  ssf_hieskoivu_kuitupuu: 'Hieskoivu kuitupuu',
  ssf_hieskoivu_tukkipuu: 'Hieskoivu tukkipuu',
  ssf_muu_lehtipuu: 'Muu lehtipuu',
  ssf_haapa_kuitupuu: 'Haapa kuitupuu',
  ssf_muu_kuitupuu: 'Muu kuitupuu',
  ssf_metsamaa: 'Metsämaa',
  ssf_kitumaa: 'Kitumaa',
  ssf_joutomaa: 'Joutomaa',
  ssf_pelto: 'Pelto',
  ssf_maatalousmaa: 'Maatalousmaa',
  ssf_tontti: 'Tontti',
  ssf_vesisto: 'Vesistö',
  ssf_muu_maa: 'Muu maa',
  ssf_kangas: 'Kangas',
  ssf_korpi: 'Korpi',
  ssf_rame: 'Räme',
  ssf_neva: 'Neva',
  ssf_ei_tiedossa: 'Ei tiedossa',
  ssf_aukea: 'Aukea',
  ssf_taimikko_alle: 'Taimikko, alle 1,3 m',
  ssf_taimikko_yli: 'Taimikko, yli 1,3 m',
  ssf_nuori_kasvatus: 'Nuori kasvatusmetsikkö',
  ssf_varttunut_kasvatus: 'Varttunut kasvatusmetsikkö',
  ssf_uudistuskypsa: 'Uudistuskypsä',
  ssf_siemenpuu: 'Siemenpuusto',
  ssf_suojuspuu: 'Suojuspuusto',
  ssf_eri_ikaisrakenteinen: 'Eri-ikäisrakenteinen',
  ssf_ylispuustoinen: 'Ylispuustoinen taimikko',
  ssf_lehto: 'Lehto',
  ssf_letto: 'Letto',
  ssf_lehtomainen: 'Lehtomainen kangas',
  ssf_tuore_kangas: 'Tuore kangas',
  ssf_kuivahko_kangas: 'Kuivahko kangas',
  ssf_kuiva_kangas: 'Kuiva kangas',
  ssf_karukkokangas: 'Karukkokangas',
  ssf_kallio_hietikko: 'Kalliomaa / hietikko',
  ssf_broker_name: 'Välittäjän nimi',
  ssf_brokerage: 'Välitysliike',
  ssf_listing_url: 'Linkki myynti-ilmoitukseen',
  ssf_schedule: 'Tarjousaika päättyy',
  ssf_description: 'Kuvaus kohteesta',

  feedback_thanks: 'Kiitos palautteestasi! Käymme palautteet läpi mahdollisimman pian.',

  // ---------------------------------------------------------------------
  // M5 — Sharing, orders, feedback queues, audit log
  // ---------------------------------------------------------------------
  admin_only: 'Vain admin-tunnukset näkevät tämän sivun.',
  nav_admin_orders: 'Arviotilaukset',
  nav_admin_feedback: 'Palautteet',
  nav_admin_audit: 'Audit-loki',

  // My estates page
  my_estates_login_required: 'Kirjaudu sisään nähdäksesi omat tilasi.',
  my_estates_tab_incoming: 'Saadut jaot',
  my_estates_tab_outgoing: 'Tekemäni jaot',
  my_estates_tab_orders: 'Tilaukseni',
  my_estates_tab_saved: 'Tallennetut',
  my_estates_saved_empty: 'Et ole tallentanut yhtään arviota.',
  my_estates_th_note: 'Muistiinpano',
  my_estates_remove: 'Poista',
  my_estates_incoming_empty: 'Sinulle ei ole jaettu vielä yhtään tilaa.',
  my_estates_outgoing_empty: 'Et ole jakanut yhtään tilaa.',
  my_estates_orders_empty: 'Et ole tehnyt vielä yhtään arviotilausta.',
  my_estates_th_estate: 'Tila',
  my_estates_th_municipality: 'Kunta',
  my_estates_th_sharer: 'Jakaja',
  my_estates_th_recipient: 'Vastaanottaja',
  my_estates_th_message: 'Viesti',
  my_estates_th_accepted: 'Avattu',
  my_estates_th_decision_note: 'Päätös',
  my_estates_open: 'Avaa',
  my_estates_revoke: 'Peruuta jako',
  my_estates_cancel_order: 'Peru tilaus',

  // Locked analysis notice (level 0 + 1 — below the map, mockup 8937)
  locked_title: 'Vertailuanalyysi vain tilauksesta',
  locked_text:
    'Käyttöoikeustasosi näyttää tilan perustiedot ja sijainnin kartalla. Saadaksesi täydellisen vertailuanalyysin (hinta-arvio, vertailutilat, AI-yhteenveto), pyydä arvio yläosan "Pyydä arviota" -painikkeesta. Järjestelmänvalvoja käsittelee tilauksesi.',

  // Share/order modals
  detail_request_order: 'Pyydä arviota',
  share_login_required: 'Kirjaudu sisään jakaaksesi tila.',
  share_recipient_prompt: 'Kenelle haluat jakaa tilan "{estate}"? Anna sähköpostiosoite:',
  share_message_prompt: 'Haluatko liittää viestin? (vapaaehtoinen)',
  share_success: 'Tila jaettu. Vastaanottaja saa ilmoituksen sähköpostilla.',
  order_login_required: 'Kirjaudu sisään tehdäksesi arviotilauksen.',
  order_message_prompt: 'Pyydä arviota tilasta "{estate}". Kerro lyhyesti miksi (vapaaehtoinen):',
  order_success: 'Arviotilauspyyntö lähetetty. Ylläpito käsittelee sen ja sinulle ilmoitetaan päätöksestä.',
  // M-FB1 / audit v2 virhe 1: "Tallenna arvio" -painike
  save_btn: 'Tallenna arvio',
  save_btn_saved: 'Tallennettu ✓',
  save_login_required: 'Kirjaudu sisään tallentaaksesi arvion.',
  save_note_prompt: 'Lisää muistiinpano tilaan "{estate}" (vapaaehtoinen):',
  save_success: 'Arvio tallennettu. Löydät sen kohdasta Omat tilat → Tallennetut.',

  // Order status pills
  order_status_pending: 'Odottaa',
  order_status_approved: 'Hyväksytty',
  order_status_rejected: 'Hylätty',
  order_status_cancelled: 'Peruutettu',

  // Admin orders
  admin_orders_title: 'Arviotilaukset',
  admin_orders_sub: 'Käyttäjien tekemät arviotilauspyynnöt.',
  admin_orders_empty: 'Ei tilauksia valitulla suodattimella.',
  admin_orders_th_requester: 'Tilaaja',
  admin_orders_note_prompt: 'Liitä päätökseen kommentti (vapaaehtoinen):',
  admin_action_approve: 'Hyväksy',
  admin_action_reject: 'Hylkää',
  order_tab_pending: 'Odottavat',
  order_tab_approved: 'Hyväksytyt',
  order_tab_rejected: 'Hylätyt',
  order_tab_all: 'Kaikki',

  // Admin feedback
  admin_feedback_title: 'Palautteet',
  admin_feedback_sub: 'Käyttäjien lähettämät palautteet.',
  admin_feedback_empty: 'Ei palautteita valitulla suodattimella.',
  admin_feedback_th_submitter: 'Lähettäjä',
  admin_feedback_th_lang: 'Kieli',
  admin_feedback_th_message: 'Viesti',
  admin_feedback_note_prompt: 'Lisää muistiinpano päätökseen (vapaaehtoinen):',
  feedback_status_new: 'Uusi',
  feedback_status_in_progress: 'Käsittelyssä',
  feedback_status_done: 'Käsitelty',
  feedback_status_archived: 'Arkistoitu',
  feedback_tab_new: 'Uudet',
  feedback_tab_in_progress: 'Käsittelyssä',
  feedback_tab_done: 'Käsitellyt',
  feedback_tab_archived: 'Arkistoidut',
  feedback_tab_all: 'Kaikki',

  // Admin audit log
  admin_audit_title: 'Audit-loki',
  admin_audit_sub: 'Kaikki ylläpidon toimet ja käyttäjien jakot/tilaukset/palautteet.',
  admin_audit_empty: 'Ei audit-merkintöjä suodattimella.',
  admin_audit_filter_action: 'Toiminto',
  admin_audit_filter_target: 'Kohteen tyyppi',
  admin_audit_apply: 'Hae',
  admin_audit_th_ts: 'Aika',
  admin_audit_th_actor: 'Tekijä',
  admin_audit_th_action: 'Toiminto',
  admin_audit_th_target: 'Kohde',
  admin_audit_th_message: 'Viesti',
  admin_audit_th_details: 'Yksityiskohdat',
};

const EN: Dict = {
  brand_mark: 'Suomen Metsätilastot Oy',
  brand_sub: 'Comparable-based pricing tool',

  auth_login: 'Sign in',
  auth_register: 'Register',
  nav_my_estates: 'My estates',
  nav_institution: 'Market overview',
  nav_admin: 'User management',
  nav_logout: 'Sign out',
  role_admin: 'Admin',
  role_institution: 'Institution',
  level_0: 'Pending',
  level_1: 'Level 1',
  level_2: 'Level 2',
  level_3: 'Level 3 (Pro)',
  level_4: 'Employee',

  search_label: 'Find an estate to price',
  search_placeholder: 'ID, name or municipality — e.g. 305-408-6-6, Kotaniemi, Kuusamo',

  listing_title: 'Estates currently for sale',
  listing_sub: 'Estates with the offer period still open',
  listing_count_one: '{n} estate',
  listing_count_many: '{n} estates',
  listing_empty: 'No results for these filters.',

  filter_region: 'Region',
  filter_all: '— all —',
  filter_min_ha: 'Min. forest area (ha)',
  filter_min_price: 'Min. asking price (€)',
  filter_min_m3: 'Min. m³/ha',
  filter_sort: 'Sort',
  filter_reset: 'Reset',
  filter_sale_status: 'Status',
  sale_status_all: 'All',
  sale_status_active: 'Currently for sale',
  sale_status_closed: 'Closed deals',

  sort_deadline: 'Deadline (soonest first)',
  sort_ha_desc: 'Area (largest first)',
  sort_ha_asc: 'Area (smallest first)',
  sort_price_desc: 'Price (highest first)',
  sort_price_asc: 'Price (lowest first)',
  sort_m3ha_desc: 'm³/ha (highest first)',
  sort_eurha_asc: '€/ha (lowest first)',
  sort_eurha_desc: '€/ha (highest first)',

  th_id: 'ID',
  th_name: 'Estate name',
  th_municipality: 'Municipality',
  th_region: 'Region',
  th_total_ha: 'Area',
  th_total_m3: 'Timber',
  th_m3ha_short: 'm³/ha',
  th_eurha_short: '€/ha',
  th_price: 'Asking price',
  th_deadline: 'Deadline',

  detail_back: '← Back to listing',
  detail_deadline_label: 'Offer deadline',
  detail_status_unsold: 'For sale',
  detail_status_sold: 'Sold',
  detail_listing_link: 'Open the original listing',
  detail_share: 'Share estimate',

  d_forest_ha: 'Forest area',
  d_total_ha: 'Total area',
  d_asking: 'Asking price',
  d_summa: 'Sum estimate',
  d_timber_value: 'Timber value',
  d_eur_ha: 'Land €/ha',
  d_m3ha: 'Timber m³/ha',
  d_eur_m3_asking: '€/m³ at asking',
  d_ainespuu: 'Industrial wood',
  d_tukki_m3: 'Sawlogs',
  d_tukki_pct: 'Sawlog share',
  d_broker: 'Broker',
  d_kauppahinta: 'Realised sale price',
  d_unknown: '—',

  detail_region: 'Region',
  detail_municipality: 'Municipality',
  detail_location: 'Location',
  detail_forest_area: 'Forest area',
  detail_timber: 'Timber',
  detail_price: 'Asking price',
  detail_sum_value: 'Sum estimate',
  detail_kauppahinta: 'Realised sale price',
  detail_source: 'Source',
  detail_source_open: 'open',

  ai_analysis_title: 'AI analysis',
  ai_analysis_empty: 'AI analysis is not yet available for this estate.',
  ai_analysis_translating: 'translating…',

  map_title: 'Estate location',
  map_sub: 'Estate on the map, {kunta}.',
  map_sub_anonymous: 'Regional centre of the estate.',
  map_attribution: '© National Land Survey of Finland',
  layer_terrain: 'Terrain',
  layer_map: 'Background',
  layer_aerial: 'Aerial',
  layer_parcels: 'Cadastral lines',
  layer_parcel_ids: 'Cadastral IDs',

  detail_description: 'Description',
  detail_remarks: 'Notes',

  view_my_estates_title: 'My estates',
  view_my_estates_sub: 'Estates shared with you and ones you have saved.',
  view_my_estates_empty: 'No saved estates yet. Sharing and bookmarking arrives in M5.',
  view_market_title: 'Market overview — Finnish forest property transactions',
  view_market_sub: 'Quarterly and annual summary of estate size, pricing and availability.',
  view_market_empty: 'Market overview arrives in M6.',
  // M6 Markkinayhteenveto / Market overview — EN
  inst_title: 'Market overview — Finnish forest property transactions',
  inst_subtitle: 'Quarterly and annual summary of estate size, pricing and availability',
  inst_mode_unsold: 'On market',
  inst_mode_sold: 'Completed sales',
  filter_kunta: 'Municipality',
  filter_kunta_ph: 'e.g. Rovaniemi',
  filter_min_ha_long: 'Min. forest area (ha)',
  filter_max_ha: 'Max. forest area (ha)',
  filter_min_m3ha: 'Min. m³/ha',
  filter_n_regions: '{n} regions selected',
  inst_quart_title: 'By quarter — area and total value',
  inst_quart_sub: 'Last {n} quarters · {first} → {last}',
  inst_quart_no_data: 'No data for current filters',
  inst_legend_ha: 'Area (ha)',
  inst_legend_eur: 'Total value (M€)',
  inst_yearly_title: 'By year',
  inst_yearly_sub: 'Yearly summary — {n} years',
  inst_region_title: 'Region-by-region summary',
  inst_region_sub: 'Distribution of estates in current filter by region',
  inst_buyer_title: 'Buyer types by year',
  inst_buyer_sub: 'Share of institutional buyers in completed sales',
  inst_buyer_no_data: 'No buyer data',
  inst_kpi_avg_title: 'Yearly average ({y1}–{y2})',
  inst_kpi_no_full_years: 'Yearly average — no data for full years',
  inst_kpi_sold_count: 'Completed sales',
  inst_kpi_unsold_count: 'Estates that came on market',
  inst_kpi_total_ha: 'Total area',
  inst_kpi_total_value: 'Total value ({label})',
  inst_kpi_avg_eur_ha: 'Avg. €/ha',
  inst_kpi_unit_count: '{mode} / year',
  inst_kpi_unit_ha: 'ha / year',
  inst_kpi_unit_eur: 'M€ / year',
  inst_kpi_unit_eur_ha: '€/ha',
  inst_count_sold: 'sales',
  inst_count_unsold: 'estates',
  inst_price_sold: 'Sale price',
  inst_price_unsold: 'Asking price',
  inst_year_total_ha: 'Total area',
  inst_year_total_sum_sold: 'Sale total',
  inst_year_total_sum_unsold: 'Asking total',
  inst_year_median_eurha: 'Median €/ha',
  inst_year_median_m3: 'Median m³/ha',
  inst_year_median_tukki: 'Median log %',
  inst_year_top_regions: 'Top regions: <strong>{regions}</strong>',
  inst_year_buyers: 'Buyers: <strong>{inst}</strong> institutional · <strong>{priv}</strong> private ({pct} % institutional)',
  inst_region_th_region: 'Region',
  inst_region_th_count: 'Estates',
  inst_region_th_ha: 'Total ha',
  inst_region_th_sum_sold: 'Total sales (M€)',
  inst_region_th_sum_unsold: 'Total asking (M€)',
  inst_region_th_eurha: 'Avg. €/ha',
  inst_buyer_info: '{pct} % inst · {inst}/{total}',
  inst_no_data: 'No data',
  market_forbidden: 'Market overview requires Pro-tier credentials.',
  // M6 saved searches
  nav_saved_searches: 'Saved searches',
  saved_searches_pick: '— Saved search —',
  saved_searches_save: 'Save search',
  saved_searches_delete: 'Delete',
  saved_searches_delete_confirm: 'Delete this saved search?',
  saved_searches_name_prompt: 'Name for this saved search:',
  saved_searches_title: 'Saved searches',
  saved_searches_sub: 'Manage your previously saved filter sets.',
  saved_searches_empty: 'No saved searches yet.',
  saved_searches_th_name: 'Name',
  saved_searches_th_filters: 'Filters',
  saved_searches_th_updated: 'Updated',
  login_required: 'Sign-in required.',
  view_admin_title: 'User management',
  view_admin_sub: 'Manage system users',
  close: 'Close',
  // M6.2 — Käyttäjähallinta modal tabs
  admin_tab_users: 'Users',
  admin_tab_create: 'New user',
  admin_tab_shares: 'Shared estates',
  admin_tab_orders: 'Orders',
  admin_tab_feedback: 'Feedback',
  admin_tab_log: 'Events',
  admin_tab_prompt: 'AI analysis guide',
  admin_prompt_sub:
    'The instructions the AI follows when writing an estate profitability analysis. Changes apply to new and re-analysed estates.',
  admin_prompt_label: 'Analysis instructions',
  admin_prompt_save: 'Save instructions',
  admin_prompt_reset: 'Restore default',
  admin_prompt_reset_confirm: 'Restore the shipped default instructions? Current edits will be lost.',
  admin_prompt_reset_done: 'Default instructions restored.',
  admin_prompt_saving: 'Saving…',
  admin_prompt_saved: 'Instructions saved.',
  admin_prompt_empty: 'Instructions cannot be empty — use "Restore default".',
  admin_prompt_is_default: 'Using the shipped default instructions.',
  admin_prompt_is_custom: 'Using customised instructions',
  admin_prompt_test_title: 'Try the instructions',
  admin_prompt_test_sub:
    'Re-runs the analysis for one estate from its stored data. Does not re-fetch the listing page.',
  admin_prompt_estate_id: 'Estate ID',
  admin_prompt_run: 'Re-analyse',
  admin_prompt_running: 'Analysing… this can take tens of seconds.',
  admin_prompt_bad_id: 'Enter a valid estate ID.',
  admin_create_first_name: 'First name',
  admin_create_last_name: 'Last name',
  admin_create_email: 'Email',
  admin_create_password: 'Password (min. 8 chars)',
  admin_create_organization: 'Organisation',
  admin_create_role: 'Role',
  admin_create_level: 'Level',
  admin_create_regions: 'Allowed regions (Level 1)',
  admin_create_submit: 'Create user',
  admin_create_ok: 'User created: {email}',
  admin_create_another: 'Create another user',
  admin_create_ok_name: 'Name',
  admin_create_ok_role: 'Role',
  admin_create_ok_level: 'Level',
  admin_edit_title: 'Edit user: {name}',
  admin_edit_status: 'Status',
  admin_edit_name: 'First and last name',
  admin_edit_email: 'Email',
  admin_edit_password: 'New password',
  admin_edit_password_hint: '(empty = no change)',
  admin_edit_save: 'Save changes',
  admin_edit_cancel: 'Cancel',
  admin_edit_delete: 'Delete user',
  admin_edit_delete_confirm: 'Permanently delete user "{name}"? This cannot be undone.',
  admin_edit_delete_ok: 'User deleted',
  admin_edit_delete_err: 'Delete failed',
  admin_edit_ok: 'Saved',
  admin_edit_err: 'Save failed',
  level_0_full: 'Pending — not yet approved',
  level_1_full: 'Level 1 — Shared estates + selected regions',
  level_2_full: 'Level 2 — All estates for sale',
  level_3_full: 'Level 3 (Pro) — Closed deals, market overview & data export',
  level_4_full: 'Employee — Pro + valuation sharing',
  view_admin_btn_edit: 'Edit',
  admin_shares_search_ph: 'Search by sharer, recipient, estate id or name…',
  admin_shares_filter_active: 'Active',
  admin_shares_filter_revoked: 'Revoked',
  admin_shares_filter_all: 'All',
  admin_shares_empty: 'No shares match this filter.',
  admin_shares_th_created: 'Created',
  admin_shares_th_estate: 'Estate',
  admin_shares_th_sharer: 'Sharer',
  admin_shares_th_recipient: 'Recipient',
  admin_shares_th_status: 'Status',
  admin_shares_th_message: 'Message',
  admin_shares_active: 'Active',
  admin_shares_revoked: 'Revoked',
  view_admin_tab_pending: 'Pending',
  view_admin_tab_active: 'Active',
  view_admin_tab_all: 'All',
  view_admin_th_name: 'Name',
  view_admin_th_email: 'Email',
  view_admin_th_role: 'Role',
  view_admin_th_level: 'Level',
  view_admin_th_status: 'Status',
  view_admin_th_created: 'Created',
  view_admin_th_actions: 'Actions',
  view_admin_btn_approve: 'Approve',
  view_admin_btn_reject: 'Reject',
  view_admin_loading: 'Loading users…',
  view_admin_empty: 'No users for this filter.',
  view_admin_err: 'Failed to load users',
  view_admin_approved: 'Approved.',
  view_admin_rejected: 'Rejected.',

  val_title: 'Comparable-based valuation',
  val_offer_title_n: 'Offer — top {n} comparable transactions',
  val_btn_asking: 'Asking-ratio',
  val_btn_summa: 'Sum-value-ratio',
  val_bid_conservative: 'Conservative',
  val_bid_recommended: 'Recommended (median)',
  val_bid_aggressive: 'Aggressive',
  val_vs_asking_pct: 'vs asking',
  val_ratio_x: 'ratio {r}x',
  val_conf_high: 'High confidence',
  val_conf_mid: 'Moderate confidence',
  val_conf_low: 'Low confidence',
  val_conf_explanation:
    '{hard} comparable transactions (same region + m³/ha ±20 %, similarity {sim} %)',
  val_based_on: 'Based on',
  val_alternative_label: 'Alternative estimate',
  val_via_summa: 'via sum-value',
  val_via_asking: 'via asking',
  val_anchor_asking: 'Asking-price anchored',
  val_anchor_summa: 'Sum-estimate anchored',
  val_anchor_label: 'Anchor',
  val_ratio: 'ratio',
  val_eur_per_ha: '€/ha (median of comparables)',
  val_confidence: 'confidence',
  val_n_comps_one: '{n} comparable',
  val_n_comps_many: '{n} comparables',
  val_similarity: 'similarity',
  val_insufficient: 'Not enough comparable transactions to compute a valuation for this estate.',
  val_forbidden: 'Comparable-based valuation requires at least Level 2 access.',

  // Sold estate view (Actual sale vs model estimate)
  val_actual_vs_model: 'Actual sale vs. model estimate',
  val_asking_price: 'Asking price',
  val_actual_price: 'Actual sale price',
  val_model_estimate: 'Model estimate (recommended price)',
  val_overshoots: 'exceeds model estimate by',
  val_undershoots: 'is below model estimate by',
  val_by_pct: '{pct} percent',
  val_diff_eur: 'diff {eur}',
  val_accuracy_good: '— the model predicted this sale well.',
  val_accuracy_mid: '— the model prediction was reasonable.',
  val_accuracy_bad: '— the model prediction deviated significantly.',
  val_based_on_asking: 'Asking price',
  val_no_comps: 'No comparables',

  reason_label: 'Recommendation rationale',
  reason_method_asking: 'Asking-price-based estimate',
  reason_method_summa: 'Sum-value-based estimate',
  median_estimate_is: 'The median estimate is',
  pct_above: 'about {pct} % above {basis}',
  pct_below: 'about {pct} % below {basis}',
  near_basis: 'near {basis}',
  of_asking: 'asking',
  of_summa: 'sum-value',
  ratio_coef: '(coefficient {r}x).',
  strategy_aggressive_html:
    'Strong positive signals support a more aggressive offer — consider <strong>{eur}</strong>.',
  strategy_conservative_html:
    'Multiple risk factors — stay close to the conservative estimate <strong>{eur}</strong>.',
  strategy_balanced_html: 'Balanced situation — the recommended estimate <strong>{eur}</strong> is a solid starting point.',
  method_explain_asking_html:
    'The recommendation is based on <strong>{n} comparables</strong> in the same area whose m³/ha matches this estate. The median favours those whose sawlog %, site-class profile and development-class profile are most similar.',
  method_explain_summa_html:
    'The recommendation is based on <strong>{n} comparables</strong> with the sale-price / sum-value ratio considered. This method measures how much the market pays vs the calculated value — the estate\u2019s own asking price does not influence the result.',
  spread_explain:
    'The conservative and aggressive estimates reflect the spread of bid coefficients in the comparable pool (±1 standard deviation).',
  bid_higher_if: 'Bid more boldly:',
  bid_lower_if: 'Bid more cautiously:',
  no_signals: 'No significant over- or under-valuation signals. Stay with the recommendation.',

  inst_label: 'Institutional<br>interest',
  inst_focus_area: 'Institutional focus area',
  inst_likely: 'Institutional buyers active',
  inst_borderline: 'Borderline case',
  inst_unlikely: 'Mainly private market',
  inst_private: 'Private market',
  inst_avg: 'Regional average',
  inst_comment_strong:
    'Strong institutional focus area — {pct}% of estates in this size range are sold to institutional buyers in this region',
  inst_comment_above_strong:
    'Estate exceeds the regional institutional-interest threshold (≥{threshold} ha) — {pct}% of similarly sized estates are sold to institutional buyers',
  inst_comment_above:
    'Estate exceeds the regional institutional-interest threshold (≥{threshold} ha) — {pct}% of similarly sized estates are sold to institutional buyers',
  inst_comment_border:
    'Borderline — estate is just below the regional institutional-interest threshold (≥{threshold} ha). Only {pct}% of similarly sized estates are sold to institutional buyers',
  inst_comment_unlikely:
    'Estates of this size rarely attract institutional buyers in this region — institutional share for this size class is only {pct}%. Regional threshold is ≥{threshold} ha',
  inst_comment_private:
    'Private market — institutional buyers generally do not participate in this region (only {pct}% of estates sold to institutions, regardless of size)',
  inst_comment_avg: 'Regional average: {pct}% institutional buyers',
  inst_n_eq: '(n={n})',

  r_inst_strong:
    'Estates of this size in this region are actively sought by institutional buyers ({pct}% of similar estates sold to institutions) — multiple professional investors compete and tend to push offers above the average',
  r_inst_likely:
    'Estate exceeds the regional institutional-interest threshold (≥{threshold} ha) — institutional buyers participate ({pct}% of similar estates)',
  r_inst_unlikely:
    'Estate does not exceed the regional institutional-interest threshold (≥{threshold} ha) — only {pct}% of similarly sized estates are sold to institutions; competition is limited to local private buyers',
  r_inst_private:
    'Institutional buyers rarely participate in this region (only {pct}% of estates regardless of size) — competition is limited to local private buyers',
  r_asking_below_summa:
    'The asking price is unusually far below the sum-value (only {pct}%) — the seller has applied large deductions, which can attract more bidders',
  r_asking_above_summa:
    'Asking price near or above the sum-value ({pct}%) — the seller has not applied the usual sum-value deductions; the estate is priced tightly',
  r_mature_high:
    'Mature and harvest-ready forest covers {pct}% of the area — immediate harvest potential supports a higher offer',
  r_mature_low: 'Little mature forest ({pct}%) — yields must wait, which lowers the estate\u2019s value',
  r_log_high: 'High sawlog share ({pct}%) — more valuable timber than average',
  r_log_low: 'Low sawlog share ({pct}%) — mostly pulpwood, lower €/m³',
  r_density_high: 'Very dense forest ({m3} m³/ha) — attracts buyers from a wider area',
  r_density_low: 'Sparse forest ({m3} m³/ha) — expect offers only from local players',
  r_site_fertile:
    'Fertile site ({pct}% grove or grove-like heath) — growth rate above average',
  r_site_poor:
    'Barren site ({pct}% dry, lichen-heath or rocky soil) — long-term yield is lower',
  r_low_confidence:
    'Only {n} comparable estates — the estimate carries large uncertainty; consider a manual review',
  r_shoreline: 'Own shoreline — recreational value can lift the price above pure forestry value',
  r_buildings: 'Buildings on the estate — can raise value; verify condition',
  r_no_road: 'No road access — harvest and management costs are higher',
  r_damage: 'Forest damage observed — may require regeneration work',
  r_protected: 'Protected area on the estate — limits forestry use',
  comp_title: 'Comparable estates',
  comp_title_n: 'Comparable estates — TOP {n}',
  comp_subtitle_top20: 'Filter: same region · m³/ha ±20 % · scoring log% + site + dev classes',
  comp_th_name: 'Estate',
  comp_th_municipality: 'Municipality',
  comp_th_score: 'Score',
  comp_th_area: 'Area',
  comp_th_m3ha: 'm³/ha',
  comp_th_tukki: 'Sawlog %',
  comp_th_price: 'Asking',
  comp_th_kauppahinta: 'Realised',
  comp_th_ratio: 'Ask r',
  comp_th_summa_ratio: 'Sum r',
  comp_th_eur_ha: '€/ha',
  comp_th_eur_m3: '€/m³',
  comp_th_closed: 'Date',

  mr_title: 'Method analysis · Asking vs. sum-anchored estimate',
  mr_subtitle: 'Background analysis of which estimator best matches realised sale prices',
  mr_summary_tag: 'SUMMARY',
  mr_summary_headline: 'The asking-anchored estimate is more accurate than the sum-anchored one regardless of size or region. Median error is 9.0 % for asking vs. 11.1 % for sum-anchored.',
  mr_summary_body: 'We tested both methods over the full sold-estate dataset (4 912 estates where both estimates could be computed). Each estate\u2019s estimate was produced by removing that estate from the comparable pool first — a genuine backtest. Comparable selection uses a ±20 % m³/ha band, which proved optimal in the backtest.',
  mr_block_accuracy: 'Overall accuracy',
  mr_th_metric: 'Metric',
  mr_th_asking: 'Asking-anchor',
  mr_th_summa: 'Sum-anchor',
  mr_metric_mae: 'Mean absolute error (MAE)',
  mr_metric_median: 'Median error',
  mr_metric_p10: 'Hits within ±10 %',
  mr_metric_p20: 'Hits within ±20 %',
  mr_metric_eur: 'Avg. error in euros',
  mr_metric_winner: 'Per-estate best estimate',
  mr_block_regions: 'By region',
  mr_regions_body: 'The asking-anchored estimate won accuracy in 15 of 18 regions. In three regions (Uusimaa, Satakunta, Pirkanmaa) the sum-anchored estimate was marginally more accurate. Only Uusimaa\u2019s gap was meaningful (6.5 pp — on a small sample, n=62). Satakunta and Pirkanmaa differed by under 1.5 pp, effectively a tie. Asking\u2019s clearest wins were Etelä-Karjala (+7.8 pp), Kainuu (+6.5 pp) and Pohjois-Karjala (+4.1 pp).',
  mr_block_size: 'Size does not matter',
  mr_size_th_class: 'Size class',
  mr_size_th_n: 'N',
  mr_size_th_asking_mae: 'Asking MAE',
  mr_size_th_summa_mae: 'Sum MAE',
  mr_size_th_diff: 'Difference',
  mr_size_lt10: 'under 10 ha',
  mr_size_10_15: '10–15 ha',
  mr_size_15_20: '15–20 ha',
  mr_size_20_30: '20–30 ha',
  mr_size_30_50: '30–50 ha',
  mr_size_50_75: '50–75 ha',
  mr_size_75_100: '75–100 ha',
  mr_size_gt100: 'over 100 ha',
  mr_size_body: 'Asking-anchored wins every size class by 1.6–12.3 percentage points. The margin is largest on under-10-ha estates (+12.3 pp, though n=12 is small) and 50–75 ha estates (+5.0 pp, n=490). Above 10 ha the asking advantage stays at a steady 1.6–5.0 pp — in no size class does the sum-anchor pull ahead.',
  mr_why_title: 'Why does the asking-anchor work better?',
  mr_why_body: 'The seller or broker has already priced the estate factoring in the sum-value, recent local sales, buildings, roads, shoreline and other particulars. The asking price therefore carries plenty of local market knowledge that a raw sum-value cannot. By predicting the "factor on the asking price" we tap into that expertise. The sum-value is more objective but loses these signals.',
  mr_recommendation_title: 'Recommendation',
  mr_recommendation_lead: 'Use the asking-anchored estimate as the default.',
  mr_recommendation_body: 'The sum-anchored estimate works as a useful cross-check — when the two estimates diverge meaningfully (over ±15 %), the gap itself is a useful signal: either the seller has priced the estate unusually, or the comparable pool contains something atypical.',
  mr_footer: 'Test methodology: Leave-one-out backtest. For each sold estate the estimate was computed by removing that estate from the comparable pool — the same logic this tool runs for unsold estates. Comparable selection used a ±20 % m³/ha band. Sample: 4 912 estates where both estimates could be computed (requires the sum-value).',

  div_title: 'Methods diverge',
  div_msg_asking_higher:
    'The asking-anchored estimate is {pct} % higher than the sum-anchored one. Recommended bump {bump} above asking.',
  div_msg_summa_higher:
    'The sum-anchored estimate is {pct} % higher than the asking one. The timber value suggests a higher realisation.',

  rec_title: 'Recommendation',
  rec_pluses: 'Plusses',
  rec_minuses: 'Minuses',
  rec_strategy_aggressive: 'Aggressive offer — toward the upper band.',
  rec_strategy_conservative: 'Conservative offer — at the lower band.',
  rec_strategy_balanced: 'Balanced offer — at the median estimate.',

  prof_species_title: 'Tree species',
  prof_species_empty: 'No species data.',
  prof_site_title: 'Site classes',
  prof_site_empty: 'No site-class data.',
  prof_dev_title: 'Development classes',
  prof_dev_empty: 'No development-class data.',
  prof_unit_m3: '{v} m³',

  login_title: 'Suomen Metsätilastot Oy',
  login_sub: 'Comparable tool — sign in',
  login_email: 'Email',
  login_password: 'Password',
  login_submit: 'Sign in',
  login_submitting: 'Signing in…',
  login_noaccount: 'No account yet?',
  login_register_here: 'Register here',
  login_err_missing: 'Enter both email and password.',
  login_err_pending: 'Your account is still waiting for admin approval.',
  login_err_suspended: 'Account is suspended. Please contact support.',
  login_err_wrong: 'Wrong email or password.',
  login_err_net: 'Network error. Please try again.',

  reg_title: 'Suomen Metsätilastot Oy',
  reg_sub: 'Register — admin will approve your account',
  reg_firstname: 'First name *',
  reg_lastname: 'Last name *',
  reg_email: 'Email *',
  reg_password: 'Password *',
  reg_password2: 'Password again *',
  reg_terms: 'I accept the terms of service and consent to Suomen Metsätilastot Oy processing my personal data under GDPR.',
  reg_submit: 'Register',
  reg_submitting: 'Submitting…',
  reg_haveaccount: 'Already have an account?',
  reg_login_here: 'Sign in here',
  reg_err_missing: 'Fill in all required fields.',
  reg_err_short_password: 'Password must be at least 8 characters.',
  reg_err_mismatch: 'Passwords do not match.',
  reg_err_terms: 'Please accept the terms and GDPR consent.',
  reg_success: 'Registration submitted. The admin will review your account and you will receive an email once approved.',

  prompt_title: 'Sign in to see this estate.',
  prompt_register_pre: 'Register',
  prompt_register_here: 'here',
  prompt_or: 'or',

  footer_version: 'v0.1.0 (M4-PoL)',
  footer_faq: 'FAQ',
  footer_feedback: 'Send feedback',
  footer_changelog: "What's new",

  faq_title: 'Frequently asked questions',

  changelog_title: "What's new",
  changelog_body: `<h3>v0.1.0 — May 2026 (M4 PoL)</h3>
<ul>
  <li>Faithful mockup port: cream/gold theme, Fraunces serif headings.</li>
  <li>Anonymous-first browsing — sign in only required to view the estimate.</li>
  <li>National Land Survey of Finland map with cadastral boundaries.</li>
  <li>Finnish/English language switch + light/dark theme, both persist.</li>
  <li>User management for admins (approve / reject pending accounts).</li>
  <li>Comparable-based valuation + table of source estates.</li>
</ul>`,

  feedback_title: 'Send feedback',
  feedback_sub: 'Describe in as much detail as possible: what you tried, what you expected, and what happened.',
  feedback_name: 'Name (optional)',
  feedback_email: 'Email (optional)',
  feedback_message: 'Message *',
  feedback_placeholder: 'For example: I clicked estate X and the map did not load…',
  feedback_submit: 'Send feedback',
  feedback_submitting: 'Sending…',
  feedback_err_empty: 'Please write a message before sending.',
  feedback_err_send: 'Sending failed. Please try again.',

  // Seller submission form ("Myyjien ilmoitukset")
  seller_submission_title: 'Seller submission',
  seller_submission_sub:
    'Fill in the sale details for the estate. The submission is sent to our admin team for review before it is published on metsatilastot.fi.',
  seller_submission_submit: 'Send submission',
  seller_submission_err_required: 'Please fill in all required fields (*).',
  seller_submission_success: 'Thank you! The submission has been received and is awaiting admin review.',

  // Seller submission form — field group titles
  ssf_g_perustiedot: 'Basic details',
  ssf_g_hintatiedot: 'Price information',
  ssf_g_lisatiedot: 'Additional details',
  ssf_g_tilavuus: 'Volume by tree species (m³)',
  ssf_g_maapohja: 'Land types (ha)',
  ssf_g_kasvupaikat: 'Site types (ha)',
  ssf_g_kehitysluokat: 'Development classes (ha)',
  ssf_g_ravinteisuus: 'Site fertility (ha)',
  ssf_g_valittaja: 'Broker',
  ssf_g_aikataulu: 'Schedule and description',

  // Seller submission form — field labels
  ssf_property_id: 'Property ID',
  ssf_name: 'Estate name',
  ssf_municipality: 'Municipality',
  ssf_region: 'Region',
  ssf_area: 'Total area (ha)',
  ssf_forest_area: 'Forest area (ha)',
  ssf_forest_type: 'Forest type',
  ssf_price: 'Asking price (€)',
  ssf_pelto_arvo: 'Field value (€)',
  ssf_tontti_rakennus_arvo: 'Plot/buildings value (€)',
  ssf_muu_arvo: 'Other value (€)',
  ssf_wood_value: 'Timber value (€)',
  ssf_sum_value: 'Sum value (€)',
  ssf_has_buildings: 'Buildings',
  ssf_has_zoning: 'Zoned area',
  ssf_zoning_type: 'Zone type',
  ssf_has_protected_area: 'Protected area',
  ssf_has_forest_damage: 'Forest damage',
  ssf_has_road_access: 'Road access',
  ssf_hunting_agreement: 'Hunting lease agreement',
  ssf_encumbrances: 'Encumbrances',
  ssf_has_pelto: 'Farmland',
  ssf_has_oma_ranta: 'Private shore',
  ssf_manty_kuitupuu: 'Pine pulpwood',
  ssf_manty_tukkipuu: 'Pine sawlog',
  ssf_kuusi_kuitupuu: 'Spruce pulpwood',
  ssf_kuusi_tukkipuu: 'Spruce sawlog',
  ssf_koivu_kuitupuu: 'Birch pulpwood',
  ssf_koivu_tukkipuu: 'Birch sawlog',
  ssf_hieskoivu_kuitupuu: 'Downy birch pulpwood',
  ssf_hieskoivu_tukkipuu: 'Downy birch sawlog',
  ssf_muu_lehtipuu: 'Other broadleaf',
  ssf_haapa_kuitupuu: 'Aspen pulpwood',
  ssf_muu_kuitupuu: 'Other pulpwood',
  ssf_metsamaa: 'Forest land',
  ssf_kitumaa: 'Scrub land',
  ssf_joutomaa: 'Wasteland',
  ssf_pelto: 'Field',
  ssf_maatalousmaa: 'Agricultural land',
  ssf_tontti: 'Plot',
  ssf_vesisto: 'Water body',
  ssf_muu_maa: 'Other land',
  ssf_kangas: 'Heath',
  ssf_korpi: 'Spruce swamp',
  ssf_rame: 'Pine bog',
  ssf_neva: 'Fen',
  ssf_ei_tiedossa: 'Unknown',
  ssf_aukea: 'Clear-cut',
  ssf_taimikko_alle: 'Seedling stand, < 1.3 m',
  ssf_taimikko_yli: 'Seedling stand, > 1.3 m',
  ssf_nuori_kasvatus: 'Young thinning stand',
  ssf_varttunut_kasvatus: 'Advanced thinning stand',
  ssf_uudistuskypsa: 'Regeneration-ready',
  ssf_siemenpuu: 'Seed tree stand',
  ssf_suojuspuu: 'Shelterwood',
  ssf_eri_ikaisrakenteinen: 'Uneven-aged',
  ssf_ylispuustoinen: 'Overstorey seedling stand',
  ssf_lehto: 'Herb-rich forest',
  ssf_letto: 'Rich birch fen',
  ssf_lehtomainen: 'Herb-rich heath',
  ssf_tuore_kangas: 'Fresh heath',
  ssf_kuivahko_kangas: 'Sub-dry heath',
  ssf_kuiva_kangas: 'Dry heath',
  ssf_karukkokangas: 'Barren heath',
  ssf_kallio_hietikko: 'Rock / sand',
  ssf_broker_name: 'Broker name',
  ssf_brokerage: 'Brokerage',
  ssf_listing_url: 'Link to listing',
  ssf_schedule: 'Offer deadline',
  ssf_description: 'Description',

  feedback_thanks: 'Thank you for your feedback! We review every message.',

  // M5 — Sharing, orders, feedback queues, audit log
  admin_only: 'Only admin accounts can view this page.',
  nav_admin_orders: 'Order requests',
  nav_admin_feedback: 'Feedback',
  nav_admin_audit: 'Audit log',

  my_estates_login_required: 'Sign in to see your estates.',
  my_estates_tab_incoming: 'Shared with me',
  my_estates_tab_outgoing: 'My shares',
  my_estates_tab_orders: 'My orders',
  my_estates_tab_saved: 'Saved',
  my_estates_saved_empty: 'You have not saved any estimates.',
  my_estates_th_note: 'Note',
  my_estates_remove: 'Remove',
  my_estates_incoming_empty: 'No estates have been shared with you yet.',
  my_estates_outgoing_empty: 'You have not shared any estates.',
  my_estates_orders_empty: 'You have not submitted any order requests yet.',
  my_estates_th_estate: 'Estate',
  my_estates_th_municipality: 'Municipality',
  my_estates_th_sharer: 'Shared by',
  my_estates_th_recipient: 'Recipient',
  my_estates_th_message: 'Message',
  my_estates_th_accepted: 'Opened',
  my_estates_th_decision_note: 'Decision',
  my_estates_open: 'Open',
  my_estates_revoke: 'Revoke',
  my_estates_cancel_order: 'Cancel order',

  locked_title: 'Comparison analysis by order only',
  locked_text:
    'Your access tier shows the estate basics and its location on the map. To get the full comparison analysis (price estimate, comparable estates, AI summary), request it from the "Request analysis" button above. An administrator will process your request.',

  detail_request_order: 'Request analysis',
  share_login_required: 'Sign in to share an estate.',
  share_recipient_prompt: 'Share estate "{estate}" — enter recipient email:',
  share_message_prompt: 'Optional message to the recipient:',
  share_success: 'Estate shared. The recipient will receive an email notification.',
  order_login_required: 'Sign in to request an analysis.',
  order_message_prompt: 'Request analysis for "{estate}". Briefly explain why (optional):',
  order_success: 'Order request submitted. An admin will review it and notify you of the decision.',
  save_btn: 'Save estimate',
  save_btn_saved: 'Saved ✓',
  save_login_required: 'Sign in to save an estimate.',
  save_note_prompt: 'Add a note for "{estate}" (optional):',
  save_success: 'Estimate saved. You can find it under My estates → Saved.',

  order_status_pending: 'Pending',
  order_status_approved: 'Approved',
  order_status_rejected: 'Rejected',
  order_status_cancelled: 'Cancelled',

  admin_orders_title: 'Order requests',
  admin_orders_sub: 'User-submitted analysis requests.',
  admin_orders_empty: 'No orders for this filter.',
  admin_orders_th_requester: 'Requester',
  admin_orders_note_prompt: 'Add a decision note (optional):',
  admin_action_approve: 'Approve',
  admin_action_reject: 'Reject',
  order_tab_pending: 'Pending',
  order_tab_approved: 'Approved',
  order_tab_rejected: 'Rejected',
  order_tab_all: 'All',

  admin_feedback_title: 'Feedback',
  admin_feedback_sub: 'Feedback submitted by users.',
  admin_feedback_empty: 'No feedback for this filter.',
  admin_feedback_th_submitter: 'Submitter',
  admin_feedback_th_lang: 'Lang',
  admin_feedback_th_message: 'Message',
  admin_feedback_note_prompt: 'Add a note (optional):',
  feedback_status_new: 'New',
  feedback_status_in_progress: 'In progress',
  feedback_status_done: 'Done',
  feedback_status_archived: 'Archived',
  feedback_tab_new: 'New',
  feedback_tab_in_progress: 'In progress',
  feedback_tab_done: 'Done',
  feedback_tab_archived: 'Archived',
  feedback_tab_all: 'All',

  admin_audit_title: 'Audit log',
  admin_audit_sub: 'All admin actions plus user shares, orders, feedback.',
  admin_audit_empty: 'No audit entries for this filter.',
  admin_audit_filter_action: 'Action',
  admin_audit_filter_target: 'Target type',
  admin_audit_apply: 'Search',
  admin_audit_th_ts: 'Time',
  admin_audit_th_actor: 'Actor',
  admin_audit_th_action: 'Action',
  admin_audit_th_target: 'Target',
  admin_audit_th_message: 'Message',
  admin_audit_th_details: 'Details',
};

const DICTS: Record<Lang, Dict> = { fi: FI, en: EN };

let current: Lang = readStored();

function readStored(): Lang {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (v === 'fi' || v === 'en') return v;
  } catch {
    /* ignore */
  }
  // Fall back to the browser preference, defaulting to Finnish.
  const nav = (typeof navigator !== 'undefined' && navigator.language) || 'fi';
  return nav.toLowerCase().startsWith('en') ? 'en' : 'fi';
}

export function getLang(): Lang {
  return current;
}

export function setLang(lang: Lang): void {
  current = lang;
  try {
    localStorage.setItem(LS_KEY, lang);
  } catch {
    /* ignore */
  }
  document.documentElement.setAttribute('lang', lang);
  applyToDOM();
  // Notify listeners (e.g. listing view re-renders rows so column headers and
  // numeric formats track the language switch).
  window.dispatchEvent(new CustomEvent('smt:lang', { detail: { lang } }));
}

export function t(key: string, vars?: Record<string, string | number>): string {
  const dict = DICTS[current];
  let s = dict[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      s = s.replaceAll(`{${k}}`, String(v));
    }
  }
  return s;
}

/**
 * Pluralised "X estates" / "X estate" helper. Both languages use the same
 * key prefix and pick singular for n === 1.
 */
export function tn(base: string, n: number): string {
  const key = n === 1 ? `${base}_one` : `${base}_many`;
  return t(key, { n });
}

/**
 * Sweep the DOM and translate every `[data-i18n]` (textContent) and
 * `[data-i18n-placeholder]` (input placeholder). Idempotent — safe to call
 * after `setLang()` and on initial bootstrap.
 */
export function applyToDOM(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    const key = el.getAttribute('data-i18n');
    if (!key) return;
    el.textContent = t(key);
  });
  root.querySelectorAll<HTMLElement>('[data-i18n-placeholder]').forEach((el) => {
    const key = el.getAttribute('data-i18n-placeholder');
    if (!key) return;
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      el.placeholder = t(key);
    }
  });
}

/** Wire the FIN/ENG toggle buttons in `#langToggle`. */
export function bindLangToggle(): void {
  const group = document.getElementById('langToggle');
  if (!group) return;
  syncToggleState();
  group.querySelectorAll<HTMLButtonElement>('button[data-lang]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const next = btn.getAttribute('data-lang') as Lang | null;
      if (!next || next === current) return;
      setLang(next);
      syncToggleState();
    });
  });
}

function syncToggleState(): void {
  document.querySelectorAll<HTMLButtonElement>('#langToggle button[data-lang]').forEach((btn) => {
    btn.classList.toggle('active', btn.getAttribute('data-lang') === current);
  });
}
